'use client';

import { useEffect, useState } from 'react';

/**
 * Cookie consent for analytics and advertising measurement.
 *
 * PECR requires consent before any non-essential cookie is set or read, so
 * nothing from Google loads, and the first-party attribution cookie is not
 * written, until the visitor chooses Accept. The choice itself is kept in
 * localStorage (not a cookie) so recording it sets nothing either.
 */
export type ConsentValue = 'granted' | 'denied';

const KEY = 'ip4_consent';
export const CONSENT_EVENT = 'ip4:consent';
export const OPEN_SETTINGS_EVENT = 'ip4:open-cookie-settings';

export function readConsent(): ConsentValue | null {
  try {
    const v = window.localStorage.getItem(KEY);
    return v === 'granted' || v === 'denied' ? v : null;
  } catch {
    return null;
  }
}

/** Remove analytics, advertising and attribution cookies after a refusal. */
export function clearTrackingCookies() {
  const names = document.cookie
    .split(';')
    .map((c) => c.split('=')[0].trim())
    .filter((n) => /^(_ga|_gid|_gat|_gcl_|ip4_attribution)/.test(n));
  const host = window.location.hostname;
  const domains = ['', host, '.' + host.replace(/^www\./, '')];
  for (const n of names) {
    for (const d of domains) {
      document.cookie = `${n}=; path=/; max-age=0${d ? `; domain=${d}` : ''}`;
    }
  }
}

export function writeConsent(value: ConsentValue) {
  const previous = readConsent();
  try {
    window.localStorage.setItem(KEY, value);
  } catch {
    // Storage unavailable: the choice applies to this page view only.
  }
  if (value === 'denied') {
    const id = process.env.NEXT_PUBLIC_GA_MEASUREMENT_ID;
    if (id) (window as unknown as Record<string, unknown>)[`ga-disable-${id}`] = true;
    clearTrackingCookies();
    // A tag that already loaded cannot be unloaded, so start clean.
    if (previous === 'granted') window.location.reload();
  }
  window.dispatchEvent(new CustomEvent(CONSENT_EVENT, { detail: value }));
}

export function openCookieSettings() {
  window.dispatchEvent(new Event(OPEN_SETTINGS_EVENT));
}

export function useConsent(): ConsentValue | null {
  const [consent, setConsent] = useState<ConsentValue | null>(null);
  useEffect(() => {
    setConsent(readConsent());
    const onChange = (e: Event) => setConsent((e as CustomEvent<ConsentValue>).detail);
    window.addEventListener(CONSENT_EVENT, onChange);
    return () => window.removeEventListener(CONSENT_EVENT, onChange);
  }, []);
  return consent;
}
